<template>
  <div>
    <EvasiveManeuverTableErrorDisplay />
    <v-expansion-panels v-model="expanded">
      <v-expansion-panel>
        <v-expansion-panel-header color="primary">
          <v-row>
            <v-icon class="ml-6 mr-4 white--text">scatter_plot</v-icon>
            <span class="font-weight-bold mt-1 text-truncate white--text"
              >Evasive scenarios
            </span>
            <exo-help-dialog
              :color="'white--text'"
              :margin="'ma-0'"
              title="Evasive solutions"
            >
              <tooltipEvasiveSolutions />
            </exo-help-dialog>
            <div class="mr-4">
              <CustomEvasiveScenarioEditButton
                :missionId="currentMission.id"
                :spaceObject="currentMission.spaceObjectTarget"
                buttonColor="red darken-3"
              />
            </div>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            dense
            :headers="evasiveManeuversHeaders"
            :items="evasiveManeuversForTable"
            :expanded.sync="evasiveManeuversExpanded"
            :loading="customEvasiveLoading"
            show-expand
            show-select
          >
            <template
              v-for="field in fieldsToDisplay"
              v-slot:[`item.${field}`]="{ item }"
            >
              {{
                getDisplayValueFromPath(
                  item[field],
                  [field],
                  'ReactionManeuverReport'
                ) || 'N/A'
              }}
            </template>
            <template v-slot:[`item.custom`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-chip
                    class="mt-1 mb-1 mr-2"
                    v-on="on"
                    :color="item.custom ? 'red' : 'darkgray'"
                    >{{ item.custom ? 'Custom' : 'Generated' }}</v-chip
                  >
                </template>
                <span>{{
                  item.custom
                    ? 'This scenario has been manually submited'
                    : 'This scenario has been generated from the given inputs'
                }}</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.buttons`]="{ item }">
              <v-row v-if="item.custom === true">
                <v-btn
                  @click="deleteCustomScenarioByName(item.id)"
                  icon
                  color="red darken-2"
                  x-small
                >
                  <v-icon>delete</v-icon>
                </v-btn>
                <CustomEvasiveScenarioEditButton
                  :scenario="
                    customEvasiveScenarios.find(
                      (scenario) => scenario.name === item.id
                    )
                  "
                  buttonColor="blue"
                  :iconOnly="true"
                  buttonIconName="edit"
                  size="x-small"
                />
              </v-row>
            </template>
            <template v-slot:[`header.data-table-select`]="{}">
              <span>Compare</span>
            </template>
            <template v-slot:[`item.data-table-select`]="{ item }">
              <v-simple-checkbox
                :value="maneuverIsInComparator(item.id)"
                @input="toggleManeuverInComparator(item.id)"
              ></v-simple-checkbox>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row
                  align="center"
                  justify="center"
                  v-for="report in item.targetImpulseManeuverReports"
                  :key="report.id"
                  ><v-col
                    ><DataPanel
                      :values="report"
                      :categories="deltaVVectorCategories"
                      :context="'ImpulseManeuverReport'"
                    ></DataPanel></v-col
                  ><v-col cols="6" /></v-row
                ><v-row class="ma-2" justify="start">
                  <v-btn
                    class="mr-4"
                    color="primary"
                    @click="exportManeuver(item.id)"
                    >Export</v-btn
                  >
                  <EvasiveManeuverCesiumDialog
                    :maneuverReport="
                      evasiveManeuverReportsWithId.find(
                        (reports) => reports.id === item.id
                      )
                    "
                    :id="item.id.toString()"
                    :key="item.id"
                  />
                </v-row></td
            ></template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dataTools from '../../tools/data.js'
import { mapGetters, mapActions } from 'vuex'
import errorMixin from '../../mixins/logHandler.js'
import DataPanel from '../Defense/DataPanel'
import EvasiveManeuverCesiumDialog from './EvasiveManeuverCesiumDialog'
import CustomEvasiveScenarioEditButton from './CustomEvasiveScenarioEditButton'
import tooltipEvasiveSolutions from '../../components/Tooltips/Defense/tooltipEvasiveSolutions'
import EvasiveManeuverTableErrorDisplay from './EvasiveManeuverTableErrorDisplay'

export default {
  name: 'EvasiveManeuverDashboard',
  mixins: [errorMixin],
  components: {
    DataPanel,
    EvasiveManeuverCesiumDialog,
    CustomEvasiveScenarioEditButton,
    tooltipEvasiveSolutions,
    EvasiveManeuverTableErrorDisplay
  },
  props: {
    computeServiceRecovery: {
      type: Boolean,
      default: false
    },
    showCustomScenarios: {
      type: Boolean,
      default: false
    },
    fieldsToDisplay: {
      type: Array,
      default: () => []
    },
    evasiveManeuverReportsWithId: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      expanded: 0,
      evasiveManeuversExpanded: [],
      deltaVVectorCategories: {
        '': {
          fields: [
            { fieldName: 'dateOfManeuver' },
            { fieldName: 'referenceFrame' },
            { fieldName: 'impulseDeltaV' },
            { fieldName: 'deltaVAlongXAxis' },
            { fieldName: 'deltaVAlongYAxis' },
            { fieldName: 'deltaVAlongZAxis' }
          ],
          noTitle: true,
          cardColor: 'transparent'
        }
      },
      displayChaserReaction: false
    }
  },

  computed: {
    ...mapGetters({
      allComparedEvasiveManeuversIds: 'allComparedEvasiveManeuversIds',
      currentResultsLog: 'currentResultsLog',
      currentMission: 'currentMission',
      customEvasiveScenarios: 'customEvasiveScenarios',
      customEvasiveLoading: 'customEvasiveLoading',
      customEvasiveErrors: 'customEvasiveErrors'
    }),

    evasiveManeuversHeaders() {
      let header = []
      for (const [fieldName, field] of Object.entries(
        resultsDescription.ReactionManeuverReport
      )) {
        if (this.fieldsToDisplay.includes(fieldName)) {
          const fieldUnit = dataTools.getDefaultGuiUnit(field.type)
          const label = fieldUnit
            ? `${field.label} (${fieldUnit})`
            : field.label
          header.push({
            text: label,
            value: fieldName,
            align: 'center'
          })
        }
      }
      if (this.showCustomScenarios) {
        header = [
          ...header,
          { text: 'Details', value: 'custom', align: 'center' },
          {
            text: '',
            value: 'buttons',
            align: 'center',
            sortable: false,
            width: '50px'
          }
        ]
      }
      return [...header, { text: '', value: 'data-table-expand' }]
    },

    evasiveManeuversForTable() {
      return this.evasiveManeuverReportsWithId.map((reports) => {
        return {
          ...reports.targetReactionManeuverReport,
          id: reports.id,
          custom: reports.custom
        }
      })
    },

    comparedManeuversStoreId() {
      return this?.currentResultsLog?.id + this?.currentResultsLog?.updatedAt
    },
    comparedManeuversIds() {
      const maneuversIds =
        this?.allComparedEvasiveManeuversIds[this.comparedManeuversStoreId]
      return maneuversIds != null ? maneuversIds : new Set()
    }
  },

  methods: {
    ...mapActions(['refreshCustomEvasiveScenarios']),

    removeManeuverFromComparator(maneuverToRemoveId) {
      this.$emit('remove-maneuver', maneuverToRemoveId)
      return maneuverToRemoveId
    },

    addManeuverToComparator(maneuverToAddId) {
      this.$emit('add-maneuver', maneuverToAddId)
      return maneuverToAddId
    },

    toggleManeuverInComparator(maneuverId) {
      if (this.maneuverIsInComparator(maneuverId)) {
        this.removeManeuverFromComparator(maneuverId)
      } else {
        this.addManeuverToComparator(maneuverId)
      }
    },

    maneuverIsInComparator(maneuverId) {
      return this.comparedManeuversIds.has(maneuverId)
    },

    getDisplayValueFromPath(value, fieldPath, context) {
      const fieldType = dataTools.getFieldTypeFromPath(fieldPath, context, null)
      if (fieldType === 'enum') {
        return dataTools.convertEnum2Display(context, fieldPath, value)
      } else {
        return dataTools.convertSi2Display(fieldType, value)
      }
    },

    exportManeuver(maneuverId) {
      this.$emit('export-maneuver', maneuverId)
    },
    deleteCustomScenarioByName(name) {
      const scenarioToDelete = this.customEvasiveScenarios.find(
        (scenario) => scenario.name === name
      )
      if (scenarioToDelete != null) {
        this.$http
          .delete(
            '/users/' +
              this.currentUser.id +
              '/customevasivescenariodefinitions/' +
              scenarioToDelete.id
          )
          .then((response) => {
            this.refreshCustomEvasiveScenarios()
          })
          .catch((e) => {
            self.displayMessage(
              'Error deleting custom scenarios. ',
              e,
              'NumericalMissionErrorDisplay',
              'error'
            )
          })
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');

.dialogContentContainer {
  padding: 30px;
  height: calc(100vh - 70px);
}

#fullScreenPlotDiv {
  width: 100%;
  height: 100%;
}
</style>
